import type { Tables } from '@fluent/types';

/**
 * Pure access-gating logic. Lives in its own module so it's exhaustively
 * unit-testable without touching React, Supabase, or Paddle. Three
 * questions the rest of the app asks via this:
 *
 *   1. derivedStatus(subscription) → which UI state is the user in?
 *   2. hasProAccess(subscription) → boolean: can they open paid content?
 *   3. isLessonGated(courseTree, lessonId) → boolean: is THIS lesson behind
 *      the paywall? (the first skill is always free)
 *
 * The rule for the first skill being free is encoded here so it has one
 * source of truth — not scattered across the home tree, the lesson player,
 * and any future analytics. CONTENT-BRIEF defines which skill IDs are free;
 * for v1 it's hard-coded to the first skill of the first unit.
 */

export type SubscriptionRow = Tables<'subscriptions'>;
export type CurrentSubscriptionRow = Tables<'current_subscription'>;

/** UI-friendly state the user is in. Maps to messaging on /profile and
 *  the paywall screen. Note that `none` and `expired` look the same to
 *  the user (no Pro access) but they trigger different copy: `none` is
 *  "you've never tried Pro" while `expired` is "your trial / sub ended". */
export type DerivedStatus =
  | 'none'        // never had a subscription
  | 'trialing'    // in 7-day free trial
  | 'active'      // paying customer in good standing
  | 'past_due'    // payment failed, Paddle retrying; no access until paid
  | 'cancelling'  // user cancelled, still inside current cycle
  | 'expired';    // cancelled or past_due dunning failed; no access

export function derivedStatus(sub: CurrentSubscriptionRow | SubscriptionRow | null | undefined): DerivedStatus {
  if (!sub) return 'none';
  // Cancelled but still in cycle = "cancelling" (we still grant access).
  // After cycle end Paddle flips status to 'canceled' → handled below.
  if (sub.cancelled_at && sub.current_period_ends_at) {
    const periodEnd = new Date(sub.current_period_ends_at).getTime();
    if (periodEnd > Date.now() && sub.status !== 'canceled') return 'cancelling';
  }
  switch (sub.status) {
    case 'trialing':
      return 'trialing';
    case 'active':
      return 'active';
    case 'past_due':
      return 'past_due';
    case 'canceled':
    case 'paused':
      return 'expired';
    default:
      return 'expired';
  }
}

/** True if the user can open paid content RIGHT NOW. The view layer reads
 *  this; the answer is also embedded in `useHasProAccess()`. Past-due is
 *  NOT Pro (Tornike 2026-10-18): every failed charge so far was an empty
 *  card after the free week, and Paddle retries for 30 days — granting
 *  past-due meant ~37 free days. Pro comes back when a retry or the "fix
 *  card" payment goes through (status → active). */
/** Pro given free by hand (provider 'gift', migration 0023): no processor,
 *  no charge date, nothing to cancel or change. Access itself needs no special
 *  case — a gift row is status 'active'. */
export function isGiftSubscription(
  sub: { provider?: string | null } | null | undefined,
): boolean {
  return sub?.provider === 'gift';
}

export function hasProAccess(sub: CurrentSubscriptionRow | SubscriptionRow | null | undefined): boolean {
  const status = derivedStatus(sub);
  return status === 'trialing' || status === 'active' || status === 'cancelling';
}

/* -------------------------------------------------------------------------
 * Lesson-level gating.
 *
 * Rule: the very first skill of the very first unit is FREE FOREVER, and so
 * is lesson 1 of every phrases-course level (2026-10-18, PHRASES.md § 3).
 * Every other lesson is Pro-gated. We resolve this from the course trees
 * at render time (not from a config) so re-ordering content auto-updates
 * the gate without code changes.
 * ------------------------------------------------------------------------- */

export interface FreeAccessShape {
  /** Lessons in the always-free tier — the first skill of the main course's
   *  first unit, plus lesson 1 of every phrases-course level. */
  freeLessonIds: ReadonlySet<string>;
  /** Every lesson is free (freeMode.ts EVERYTHING_FREE). */
  everything?: boolean;
}

/** The slice of a course tree the free tier reads. */
export interface FreeAccessTree {
  units: ReadonlyArray<{
    resolvedSkills: ReadonlyArray<{ resolvedLessons: ReadonlyArray<{ id: string }> }>;
  }>;
}

/** Build the free-access set. Memoize at the call-site if you read this
 *  often; the function itself is cheap (one set build).
 *
 *  - Main course (`tree`): the first skill of the first unit (4 lessons).
 *  - Phrases course (`phrasesTree`, PHRASES.md § 3): lesson 1 of every level
 *    (one unit per level) — a learner can taste A1 … C2 before paying. Only
 *    ids are read, so the course OUTLINE (course.ts `getCourseOutline`) is
 *    enough: no need to load the lazy phrases lessons.
 *  `isLessonGated` stays the single check every gate point uses. */
export function buildFreeAccess(
  tree: FreeAccessTree | null,
  phrasesTree?: FreeAccessTree | null,
  opts: { everything?: boolean } = {},
): FreeAccessShape {
  const ids = new Set<string>();
  const firstSkill = tree?.units[0]?.resolvedSkills[0];
  if (firstSkill) {
    for (const lesson of firstSkill.resolvedLessons) ids.add(lesson.id);
  }
  for (const level of phrasesTree?.units ?? []) {
    const first = level.resolvedSkills[0]?.resolvedLessons[0];
    if (first) ids.add(first.id);
  }
  return { freeLessonIds: ids, everything: opts.everything ?? false };
}

/** True if this specific lesson is locked behind Pro. */
export function isLessonGated(access: FreeAccessShape, lessonId: string): boolean {
  if (access.everything) return false;
  return !access.freeLessonIds.has(lessonId);
}

/** Combined check: can THIS user play THIS lesson right now? */
export function canPlayLesson(
  sub: CurrentSubscriptionRow | SubscriptionRow | null | undefined,
  access: FreeAccessShape,
  lessonId: string,
): boolean {
  if (!isLessonGated(access, lessonId)) return true; // free tier
  return hasProAccess(sub);
}

/* -------------------------------------------------------------------------
 * Tri-state gating — "we don't know yet" is a real answer.
 *
 * `hasProAccess(undefined)` is false, which is correct for a user with no
 * subscription and WRONG for a subscriber whose row is still in flight. A
 * screen that only sees the boolean cannot tell those apart, so on every
 * cold load of a Pro lesson it flashed the paywall at a paying customer
 * (and fired `paywall_shown` at them). The booleans above stay exactly as
 * they were — screens that legitimately treat "unknown" as "not Pro"
 * (Profile copy, award progress) keep using them. Screens that GATE
 * content read the tri-state below and render a skeleton on 'loading'.
 * ------------------------------------------------------------------------- */

export type AccessState = 'loading' | 'yes' | 'no';

/** The slice of a React-Query result the gate needs. Declared structurally
 *  so this module stays React-free (and unit-testable without a renderer). */
export interface SubscriptionQuery {
  isPending: boolean;
  isError: boolean;
  data: CurrentSubscriptionRow | SubscriptionRow | null | undefined;
}

/** Pro access as a tri-state.
 *
 *  'loading' while the read is in flight with nothing cached. An ERROR is
 *  deliberately NOT 'loading': after the retries are spent we have to answer
 *  something, and an eternal skeleton with no way out is worse than the
 *  paywall — which at least offers "restore" and a way home. Cached data
 *  wins over a failed refetch (React Query keeps `data` on error), so a
 *  subscriber who is briefly offline keeps their access. */
export function proAccessState(q: SubscriptionQuery): AccessState {
  if (q.isPending) return 'loading';
  return hasProAccess(q.data ?? null) ? 'yes' : 'no';
}

/** Can THIS user play THIS lesson — tri-state. A free lesson answers 'yes'
 *  immediately: it never depends on the subscription, so making the pilot
 *  lesson wait on a network read would be a skeleton for nothing. */
export function lessonAccessState(
  q: SubscriptionQuery,
  access: FreeAccessShape,
  lessonId: string,
): AccessState {
  if (!isLessonGated(access, lessonId)) return 'yes';
  return proAccessState(q);
}
